/**
 * AES-256-GCM (NIST SP 800-38D) - Web Crypto 実装。
 *
 * 2 種類の wire format を提供する:
 *
 *   3-part (既存): { iv: base64, ciphertext: base64, authTag: base64 }
 *     → apps/api と互換。将来の Vault Item 用。
 *
 *   Sealed 2-part (C-7b 追加): ciphertext || authTag を 1 本の Uint8Array
 *     → protectedSymmetricKey / protectedSymmetricKeyIv の wire format に対応。
 *     → aesGcmSeal / aesGcmOpen で扱う。
 *
 * 共通注意:
 *   - Web Crypto の encrypt は ciphertext + authTag を **連結した** ArrayBuffer を返す
 *   - importKey は extractable=false で行い、JS ヒープに鍵 bytes を残さない
 *   - IV は呼び出し側で必ずランダム生成し、再利用しない (鍵漏洩に直結)
 */
import { AES_GCM } from './constants'
import { base64ToBytes, bytesToBase64, concatBytes } from './encoding'
import type { AesGcmPayload } from './types'

const subtle = (): SubtleCrypto => {
  if (typeof globalThis.crypto?.subtle === 'undefined') {
    throw new Error('Web Crypto API (crypto.subtle) is not available')
  }

  return globalThis.crypto.subtle
}

const importGcmKey = async (rawKey: Uint8Array): Promise<CryptoKey> => {
  if (rawKey.length !== AES_GCM.KEY_BYTES) {
    throw new RangeError(`AES-256-GCM key must be ${AES_GCM.KEY_BYTES} bytes, got ${rawKey.length}`)
  }

  return subtle().importKey(
    'raw',
    rawKey as Uint8Array<ArrayBuffer>,
    { name: 'AES-GCM' },
    /* extractable */ false,
    ['encrypt', 'decrypt'],
  )
}

/**
 * AES-256-GCM 暗号化。
 *
 * @param key       32 byte の対称鍵 (Uint8Array)
 * @param iv        12 byte のランダム IV (毎回 randomBytes(12) で新規生成すること)
 * @param plaintext 平文 (Uint8Array)
 * @param aad       Additional Authenticated Data (省略可)
 */
export const aesGcmEncrypt = async (
  key: Uint8Array,
  iv: Uint8Array,
  plaintext: Uint8Array,
  aad?: Uint8Array,
): Promise<AesGcmPayload> => {
  if (iv.length !== AES_GCM.IV_BYTES) {
    throw new RangeError(`IV must be ${AES_GCM.IV_BYTES} bytes`)
  }
  const cryptoKey = await importGcmKey(key)

  const algo: AesGcmParams = {
    name: 'AES-GCM',
    iv: iv as Uint8Array<ArrayBuffer>,
    tagLength: AES_GCM.AUTH_TAG_BITS,
    ...(aad && aad.length > 0 ? { additionalData: aad as Uint8Array<ArrayBuffer> } : {}),
  }

  const encrypted = new Uint8Array(
    await subtle().encrypt(algo, cryptoKey, plaintext as Uint8Array<ArrayBuffer>),
  )

  // Web Crypto の出力末尾 16 byte が GCM 認証タグ。backend と同形に分離する。
  const tagOffset = encrypted.length - AES_GCM.AUTH_TAG_BYTES
  const ciphertext = encrypted.subarray(0, tagOffset)
  const authTag = encrypted.subarray(tagOffset)

  return {
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(ciphertext),
    authTag: bytesToBase64(authTag),
  }
}

/**
 * AES-256-GCM 復号。
 *
 * 認証タグ検証に失敗した場合 (改ざん / 鍵違い / IV 違い / AAD 違い) は
 * Web Crypto 自身が `OperationError` を throw する。本ラッパーは握りつぶさず再送出する。
 */
export const aesGcmDecrypt = async (
  key: Uint8Array,
  payload: AesGcmPayload,
  aad?: Uint8Array,
): Promise<Uint8Array> => {
  const iv = base64ToBytes(payload.iv)
  const authTag = base64ToBytes(payload.authTag)
  const ciphertext = base64ToBytes(payload.ciphertext)

  if (iv.length !== AES_GCM.IV_BYTES) {
    throw new RangeError(`IV must be ${AES_GCM.IV_BYTES} bytes`)
  }
  if (authTag.length !== AES_GCM.AUTH_TAG_BYTES) {
    throw new RangeError(`auth tag must be ${AES_GCM.AUTH_TAG_BYTES} bytes`)
  }

  const cryptoKey = await importGcmKey(key)

  // Web Crypto は ciphertext + authTag を 1 本の ArrayBuffer として要求する
  const sealed = concatBytes(ciphertext, authTag)

  const algo: AesGcmParams = {
    name: 'AES-GCM',
    iv: iv as Uint8Array<ArrayBuffer>,
    tagLength: AES_GCM.AUTH_TAG_BITS,
    ...(aad && aad.length > 0 ? { additionalData: aad as Uint8Array<ArrayBuffer> } : {}),
  }

  return new Uint8Array(await subtle().decrypt(algo, cryptoKey, sealed as Uint8Array<ArrayBuffer>))
}

// ── Sealed (2-part) API ──────────────────────────────────────────
// KEY_HIERARCHY.md §4.1 step 7: protectedSymmetricKey = ciphertext || authTag (1 blob)
// IV は別フィールド (protectedSymmetricKeyIv) で保管する。

/**
 * AES-256-GCM で plaintext を暗号化し、`ciphertext || authTag` を 1 本の
 * Uint8Array (= sealed) で返す。
 *
 * 用途: Symmetric Key (SK) の wrap (KEY_HIERARCHY.md §4.1 step 7)。
 * 返値のバイト長 = plaintext.length + 16 (auth tag)。
 */
export const aesGcmSeal = async (
  key: Uint8Array,
  iv: Uint8Array,
  plaintext: Uint8Array,
): Promise<Uint8Array> => {
  if (iv.length !== AES_GCM.IV_BYTES) {
    throw new RangeError(`IV must be ${AES_GCM.IV_BYTES} bytes`)
  }
  const cryptoKey = await importGcmKey(key)

  const algo: AesGcmParams = {
    name: 'AES-GCM',
    iv: iv as Uint8Array<ArrayBuffer>,
    tagLength: AES_GCM.AUTH_TAG_BITS,
  }

  return new Uint8Array(
    await subtle().encrypt(algo, cryptoKey, plaintext as Uint8Array<ArrayBuffer>),
  )
}

/**
 * `aesGcmSeal` の逆操作。sealed = ciphertext || authTag を復号する。
 *
 * 認証タグ検証失敗 (改ざん / 鍵違い / IV 違い) は Web Crypto が OperationError を throw する。
 */
export const aesGcmOpen = async (
  key: Uint8Array,
  iv: Uint8Array,
  sealed: Uint8Array,
): Promise<Uint8Array> => {
  if (iv.length !== AES_GCM.IV_BYTES) {
    throw new RangeError(`IV must be ${AES_GCM.IV_BYTES} bytes`)
  }
  if (sealed.length <= AES_GCM.AUTH_TAG_BYTES) {
    throw new RangeError(
      `sealed data too short: expected > ${AES_GCM.AUTH_TAG_BYTES} bytes, got ${sealed.length}`,
    )
  }
  const cryptoKey = await importGcmKey(key)

  const algo: AesGcmParams = {
    name: 'AES-GCM',
    iv: iv as Uint8Array<ArrayBuffer>,
    tagLength: AES_GCM.AUTH_TAG_BITS,
  }

  return new Uint8Array(await subtle().decrypt(algo, cryptoKey, sealed as Uint8Array<ArrayBuffer>))
}
